import { AppLanguage, detectLanguage } from './language';

const en = {
  openProject: 'Open a video project before animating a clip.',
  selectVideoFile: 'Select a video file for live-action animation.',
  sourceRequired: 'Upload or select a live-action video first.',
  sourceUnreadable: 'Could not play this video. Try a different clip.',
  waitForDuration: 'Wait for the video duration to load, then try again.',
  promptRequired: 'Choose a style or add some extra direction.',
  sourceTooLarge:
    'Live-action clips must be {maxMb} MB or smaller. This clip is {sizeMb} MB.',
  sourceTooLong: 'Live-action clips must be {maxSeconds} seconds or shorter.',
  sourceTooLongActual:
    'Live-action clips must be {maxSeconds} seconds or shorter. This clip is {actualSeconds}s.',
  sourceTooShort: 'Live-action clips must be at least {minSeconds} seconds.',
  sourceTooShortActual:
    'Live-action clips must be at least {minSeconds} seconds. This clip is {actualSeconds}s.',
  promptTooLong: 'The prompt is limited to {maxChars} characters.',
  styleDirectionTooLong:
    'Extra direction is limited to {limit} characters with this style.',
  imageAspectRatio:
    'One of the selected images has an aspect ratio of {ratio}. Images must be between {min} and {max}. Crop it and try again.',
} as const;

export type LiveActionMessageKey = keyof typeof en;
export type LiveActionMessageVars = Record<string, string | number>;

const zh: Record<LiveActionMessageKey, string> = {
  openProject: '请先打开一个视频项目再制作动画。',
  selectVideoFile: '请选择用于实拍转动画的视频文件。',
  sourceRequired: '请先上传或选择一段实拍视频。',
  sourceUnreadable: '无法播放此视频，请尝试其他片段。',
  waitForDuration: '请等待视频时长加载完成后再试。',
  promptRequired: '请选择一种风格或添加额外描述。',
  sourceTooLarge: '实拍片段必须不超过 {maxMb} MB。此片段为 {sizeMb} MB。',
  sourceTooLong: '实拍片段必须不超过 {maxSeconds} 秒。',
  sourceTooLongActual:
    '实拍片段必须不超过 {maxSeconds} 秒。此片段为 {actualSeconds} 秒。',
  sourceTooShort: '实拍片段至少需要 {minSeconds} 秒。',
  sourceTooShortActual:
    '实拍片段至少需要 {minSeconds} 秒。此片段为 {actualSeconds} 秒。',
  promptTooLong: '提示词最多 {maxChars} 个字符。',
  styleDirectionTooLong: '使用此风格时，额外描述最多 {limit} 个字符。',
  imageAspectRatio:
    '所选图片中有一张的宽高比为 {ratio}。图片宽高比必须在 {min} 到 {max} 之间。请裁剪后重试。',
};

const es: Record<LiveActionMessageKey, string> = {
  openProject: 'Abre un proyecto de video antes de animar un clip.',
  selectVideoFile:
    'Selecciona un archivo de video para la animación de acción real.',
  sourceRequired: 'Sube o selecciona primero un video de acción real.',
  sourceUnreadable: 'No se pudo reproducir este video. Prueba con otro clip.',
  waitForDuration:
    'Espera a que se cargue la duración del video e inténtalo de nuevo.',
  promptRequired: 'Elige un estilo o añade alguna indicación adicional.',
  sourceTooLarge:
    'Los clips de acción real deben ser de {maxMb} MB o menos. Este clip tiene {sizeMb} MB.',
  sourceTooLong:
    'Los clips de acción real deben durar {maxSeconds} segundos o menos.',
  sourceTooLongActual:
    'Los clips de acción real deben durar {maxSeconds} segundos o menos. Este clip dura {actualSeconds} s.',
  sourceTooShort:
    'Los clips de acción real deben durar al menos {minSeconds} segundos.',
  sourceTooShortActual:
    'Los clips de acción real deben durar al menos {minSeconds} segundos. Este clip dura {actualSeconds} s.',
  promptTooLong: 'El prompt se limita a {maxChars} caracteres.',
  styleDirectionTooLong:
    'La indicación adicional se limita a {limit} caracteres con este estilo.',
  imageAspectRatio:
    'Una de las imágenes seleccionadas tiene una relación de aspecto de {ratio}. Las imágenes deben estar entre {min} y {max}. Recórtala e inténtalo de nuevo.',
};

const fr: Record<LiveActionMessageKey, string> = {
  openProject: "Ouvrez un projet vidéo avant d'animer un clip.",
  selectVideoFile:
    "Sélectionnez un fichier vidéo pour l'animation en prise de vue réelle.",
  sourceRequired:
    "Importez ou sélectionnez d'abord une vidéo en prise de vue réelle.",
  sourceUnreadable: 'Impossible de lire cette vidéo. Essayez un autre clip.',
  waitForDuration:
    'Attendez le chargement de la durée de la vidéo, puis réessayez.',
  promptRequired:
    'Choisissez un style ou ajoutez des indications supplémentaires.',
  sourceTooLarge:
    'Les clips en prise de vue réelle doivent faire {maxMb} Mo ou moins. Ce clip fait {sizeMb} Mo.',
  sourceTooLong:
    'Les clips en prise de vue réelle doivent durer {maxSeconds} secondes ou moins.',
  sourceTooLongActual:
    'Les clips en prise de vue réelle doivent durer {maxSeconds} secondes ou moins. Ce clip dure {actualSeconds} s.',
  sourceTooShort:
    'Les clips en prise de vue réelle doivent durer au moins {minSeconds} secondes.',
  sourceTooShortActual:
    'Les clips en prise de vue réelle doivent durer au moins {minSeconds} secondes. Ce clip dure {actualSeconds} s.',
  promptTooLong: 'Le prompt est limité à {maxChars} caractères.',
  styleDirectionTooLong:
    'Les indications supplémentaires sont limitées à {limit} caractères avec ce style.',
  imageAspectRatio:
    'L’une des images sélectionnées a un rapport d’aspect de {ratio}. Les images doivent être comprises entre {min} et {max}. Recadrez-la et réessayez.',
};

const de: Record<LiveActionMessageKey, string> = {
  openProject: 'Öffne zuerst ein Videoprojekt, bevor du einen Clip animierst.',
  selectVideoFile: 'Wähle eine Videodatei für die Live-Action-Animation aus.',
  sourceRequired:
    'Lade zuerst ein Live-Action-Video hoch oder wähle eines aus.',
  sourceUnreadable:
    'Dieses Video konnte nicht abgespielt werden. Versuche es mit einem anderen Clip.',
  waitForDuration:
    'Warte, bis die Videodauer geladen ist, und versuche es dann erneut.',
  promptRequired: 'Wähle einen Stil oder füge zusätzliche Anweisungen hinzu.',
  sourceTooLarge:
    'Live-Action-Clips dürfen höchstens {maxMb} MB groß sein. Dieser Clip ist {sizeMb} MB groß.',
  sourceTooLong:
    'Live-Action-Clips dürfen höchstens {maxSeconds} Sekunden lang sein.',
  sourceTooLongActual:
    'Live-Action-Clips dürfen höchstens {maxSeconds} Sekunden lang sein. Dieser Clip ist {actualSeconds} s lang.',
  sourceTooShort:
    'Live-Action-Clips müssen mindestens {minSeconds} Sekunden lang sein.',
  sourceTooShortActual:
    'Live-Action-Clips müssen mindestens {minSeconds} Sekunden lang sein. Dieser Clip ist {actualSeconds} s lang.',
  promptTooLong: 'Der Prompt ist auf {maxChars} Zeichen begrenzt.',
  styleDirectionTooLong:
    'Zusätzliche Anweisungen sind mit diesem Stil auf {limit} Zeichen begrenzt.',
  imageAspectRatio:
    'Eines der ausgewählten Bilder hat ein Seitenverhältnis von {ratio}. Bilder müssen zwischen {min} und {max} liegen. Schneide es zu und versuche es erneut.',
};

const pt: Record<LiveActionMessageKey, string> = {
  openProject: 'Abra um projeto de vídeo antes de animar um clipe.',
  selectVideoFile:
    'Selecione um arquivo de vídeo para a animação de ação ao vivo.',
  sourceRequired: 'Envie ou selecione primeiro um vídeo de ação ao vivo.',
  sourceUnreadable:
    'Não foi possível reproduzir este vídeo. Tente outro clipe.',
  waitForDuration:
    'Aguarde o carregamento da duração do vídeo e tente novamente.',
  promptRequired: 'Escolha um estilo ou adicione instruções extras.',
  sourceTooLarge:
    'Os clipes de ação ao vivo devem ter {maxMb} MB ou menos. Este clipe tem {sizeMb} MB.',
  sourceTooLong:
    'Os clipes de ação ao vivo devem ter {maxSeconds} segundos ou menos.',
  sourceTooLongActual:
    'Os clipes de ação ao vivo devem ter {maxSeconds} segundos ou menos. Este clipe tem {actualSeconds} s.',
  sourceTooShort:
    'Os clipes de ação ao vivo devem ter pelo menos {minSeconds} segundos.',
  sourceTooShortActual:
    'Os clipes de ação ao vivo devem ter pelo menos {minSeconds} segundos. Este clipe tem {actualSeconds} s.',
  promptTooLong: 'O prompt é limitado a {maxChars} caracteres.',
  styleDirectionTooLong:
    'As instruções extras são limitadas a {limit} caracteres com este estilo.',
  imageAspectRatio:
    'Uma das imagens selecionadas tem uma proporção de {ratio}. As imagens devem ter entre {min} e {max}. Recorte-a e tente novamente.',
};

const ja: Record<LiveActionMessageKey, string> = {
  openProject:
    'クリップをアニメーション化する前に動画プロジェクトを開いてください。',
  selectVideoFile: '実写アニメーション用の動画ファイルを選択してください。',
  sourceRequired: '先に実写動画をアップロードするか選択してください。',
  sourceUnreadable:
    'この動画を再生できませんでした。別のクリップを試してください。',
  waitForDuration:
    '動画の長さが読み込まれるまで待ってから、もう一度お試しください。',
  promptRequired: 'スタイルを選択するか、追加の指示を入力してください。',
  sourceTooLarge:
    '実写クリップは{maxMb}MB以下にしてください。このクリップは{sizeMb}MBです。',
  sourceTooLong: '実写クリップは{maxSeconds}秒以下にしてください。',
  sourceTooLongActual:
    '実写クリップは{maxSeconds}秒以下にしてください。このクリップは{actualSeconds}秒です。',
  sourceTooShort: '実写クリップは{minSeconds}秒以上にしてください。',
  sourceTooShortActual:
    '実写クリップは{minSeconds}秒以上にしてください。このクリップは{actualSeconds}秒です。',
  promptTooLong: 'プロンプトは{maxChars}文字以内にしてください。',
  styleDirectionTooLong:
    'このスタイルでは追加の指示は{limit}文字以内にしてください。',
  imageAspectRatio:
    '選択した画像の1つのアスペクト比は{ratio}です。画像は{min}～{max}の範囲にしてください。切り抜いて再試行してください。',
};

const MESSAGES: Record<AppLanguage, Record<LiveActionMessageKey, string>> = {
  en,
  zh,
  es,
  fr,
  de,
  pt,
  ja,
};

export function liveActionMessage(
  key: LiveActionMessageKey,
  vars: LiveActionMessageVars = {},
  language: AppLanguage = detectLanguage()
): string {
  const template = MESSAGES[language]?.[key] ?? en[key];
  return template.replace(/\{(\w+)\}/g, (_, name: string) =>
    String(vars[name] ?? '')
  );
}

export function formatLiveActionNumber(
  value: number,
  maximumFractionDigits = 0,
  language: AppLanguage = detectLanguage()
): string {
  return new Intl.NumberFormat(language, {
    maximumFractionDigits,
  }).format(value);
}
